import { select, put, call } from 'redux-saga/effects';
import { ee } from '../../../../services/earth-engine';

import { evaluate } from '../../../tools/effects';

import { Types } from '../header';
import { Actions as Results } from '../../results';
import { Actions as Map } from '../../map';

import * as Selectors from '../../../../selectors';
import { generateBrBGColors } from '../../../../common/utils';
import * as Metadata from '../../../../common/metadata';
import * as Coastline from '../../../../algorithms/shoreline';
import { applyRollingSmooth } from '../../../../app/components/visualization/shoreR/utils';

import { deriveInChunks, ResultsCacheHolder } from './shoreline';

/**
 * @description Static cache holding the Earth Engine handles and raw results from
 * `performShoreRAnalysis`, so `performShoreRAnalysisCommit` can re-run the statistics after
 * excluding outlier dates without recomputing the transects/shorelines from scratch.
 * @see {@link performShoreRAnalysis}
 * @see {@link performShoreRAnalysisCommit}
 * @memberof module:ShoreR
 */
export class ShoreRAnalysisCache {
  static transects = null;
  static baseline = null;
  static sds = null;
  static satellite = null;
  static roi = null;
  static selectedImages = null;
  static rawResults = null;
  static rawTransectData = null;
  static shorelinesGeoJSON = null;

  /**
   * @function reset
   * @description Clears every cached field back to null.
   * **Called by:
   * @see {performShoreRAnalysis}
   * @memberof module:ShoreR
   * @returns {void}
   */
  static reset() {
    ShoreRAnalysisCache.transects = null;
    ShoreRAnalysisCache.baseline = null;
    ShoreRAnalysisCache.sds = null;
    ShoreRAnalysisCache.satellite = null;
    ShoreRAnalysisCache.roi = null;
    ShoreRAnalysisCache.selectedImages = null;
    ShoreRAnalysisCache.rawResults = null;
    ShoreRAnalysisCache.rawTransectData = null;
    ShoreRAnalysisCache.shorelinesGeoJSON = null;
  }
}

/**
 * @function performShoreRAnalysis
 * @description Generates transects along the baseline, extracts shorelines, and computes raw
 * transect statistics — but does NOT push the final `shoreRData` or render map layers.
 * 1. Generates orthogonal transects along the baseline and derives shorelines from the selected
 *    images (in chunks).
 * 2. Computes the raw per-transect statistics and stashes every GEE handle needed to redo them
 *    in `ShoreRAnalysisCache`.
 * 3. Publishes `shoreRRawData` for the outlier-detection UI to consume, then dispatches
 *    `performShoreRAnalysisCommit([])` so the flow continues automatically for callers that don't
 *    drive the outlier confirmation UI.
 * @param {Object} params.payload - Action payload.
 * @param {Array} params.payload.coordinatesBaseline - Baseline coordinates.
 * @param {Number} params.payload.spacing - Distance between transects, in meters.
 * @param {Number} params.payload.extent - Transect length, in meters.
 * @param {Array} params.payload.selectedImages - Selected images to derive shorelines from.
 * @param {Number} params.payload.threshold - Water/land threshold for shoreline extraction.
 * @generator
 * @yields {Object} select(getAcquisitionParameters) - Reads the satellite configuration.
 * @yields {Object} Results.push('baselineData') - Dispatches the baseline coordinates/geometry.
 * @yields {Promise} call(deriveInChunks) - Derives shorelines from the selected images.
 * @yields {Promise} call(Coastline.generateTransectsStatistics) - Computes raw per-transect statistics.
 * @yields {Object} Results.push('shoreRRawData') - Dispatches the raw transect data.
 * @yields {Object} PERFORM_SHORE_R_ANALYSIS_COMMIT - Continues the flow with no dates excluded.
 * @see {@link ShoreRAnalysisCache}
 * @see {@link deriveInChunks}
 * @see {@link performShoreRAnalysisCommit}
 * **Called by:
 * @see {ShoreRAnalysisSteps} - via the "Run analysis" button's onClick.
 * @memberof module:ShoreR
 * @returns {void}
 */
export const performShoreRAnalysis = function* ({
  payload: { coordinatesBaseline, spacing, extent, selectedImages, threshold },
}) {
  ShoreRAnalysisCache.reset();

  const { satellite } = yield select(Selectors.getAcquisitionParameters);
  const baseline = ee.Geometry.LineString(coordinatesBaseline);
  const transects = yield call(Coastline.generateOrthogonalTransects, coordinatesBaseline, spacing, extent);
  ResultsCacheHolder.transacts = transects;
  threshold = parseFloat(threshold);
  yield put(
    Results.push('baselineData', {
      coords: coordinatesBaseline,
      baseline: baseline,
    }),
  );

  const roi = baseline.buffer(extent / 2);

  const sds = yield call(deriveInChunks, selectedImages, satellite, roi, 30, threshold, transects);
  if (!sds) return;
  ResultsCacheHolder.shorelines = sds;

  const rawResults = yield call(Coastline.generateTransectsStatistics, transects, baseline, sds, []);

  const rawTransectData = rawResults.getInfo();

  ShoreRAnalysisCache.transects = transects;
  ShoreRAnalysisCache.baseline = baseline;
  ShoreRAnalysisCache.sds = sds;
  ShoreRAnalysisCache.satellite = satellite;
  ShoreRAnalysisCache.roi = roi;
  ShoreRAnalysisCache.selectedImages = selectedImages;
  ShoreRAnalysisCache.rawResults = rawResults;
  ShoreRAnalysisCache.rawTransectData = rawTransectData;

  yield put(
    Results.push('shoreRRawData', {
      transectData: rawTransectData,
    }),
  );

  yield put({
    type: Types.PERFORM_SHORE_R_ANALYSIS_COMMIT,
    payload: { datesToRemove: [] },
  });
};

/**
 * @function performShoreRAnalysisCommit
 * @description Given a list of dates flagged for removal, filters the cached shoreline
 * FeatureCollection, optionally re-runs `generateTransectsStatistics` so slope/LRR/R² refit
 * excluding the removed dates, then renders the map layers and pushes the final `shoreRData` to
 * redux (with each transect's rates spatially smoothed).
 * @param {Object} params.payload - Action payload.
 * @param {Array<Number>} params.payload.datesToRemove - `system:time_start` values of shorelines to exclude.
 * @generator
 * @yields {Promise} call(Coastline.generateTransectsStatistics) - Recomputes statistics excluding the removed dates.
 * @yields {Promise} call(Coastline.expandHorizontally) - Builds the transect visualization polygons.
 * @yields {Promise} call(Coastline.summaryShorelineStatistics) - Builds the summary shoreline geometries.
 * @yields {Promise} evaluate(...) - Evaluates each transect's Esteves label for coloring.
 * @yields {Object} Map.removeShapeGroup - Clears the previous shoreline/transect map layers.
 * @yields {Object} Map.addEEFeature - Adds the new shoreline/transect map layers.
 * @yields {Object} Results.push('shoreRData') - Dispatches the final transect/shoreline data.
 * @see {@link ShoreRAnalysisCache}
 * @see {@link applyRollingSmooth}
 * **Called by:
 * @see {performShoreRAnalysis} - with an empty exclusion list.
 * @see {ShoreRAnalysisSteps} - via handleOutlierConfirm, with the user-selected outlier dates.
 * @memberof module:ShoreR
 * @returns {void}
 */
export const performShoreRAnalysisCommit = function* ({ payload: { datesToRemove } }) {
  const { transects, baseline, sds, selectedImages, roi, rawResults } = ShoreRAnalysisCache;

  if (!transects || !baseline || !sds) {
    console.error(
      '[ShoreR] performShoreRAnalysisCommit called without analysis cache. ' + 'Run performShoreRAnalysis first.',
    );
    return;
  }

  const willFilter = Array.isArray(datesToRemove) && datesToRemove.length > 0;

  let finalSds = sds;
  let finalResults = rawResults;

  if (willFilter) {
    const removeList = ee.List(datesToRemove.map((d) => Number(d)));
    finalSds = sds.filter(ee.Filter.inList('system:time_start', removeList).not());

    finalResults = yield call(Coastline.generateTransectsStatistics, transects, baseline, finalSds, []);
  }

  const transectsViz = yield call(Coastline.expandHorizontally, finalResults, 10);
  let shorelines = yield call(Coastline.summaryShorelineStatistics, finalResults, finalSds, roi);

  ResultsCacheHolder.shorelines = finalSds;

  const shoreColors = generateBrBGColors(selectedImages.length);

  const transectLabels = yield evaluate(finalResults.map((f) => ee.Feature(f).get('label')));
  const transectColors = transectLabels.map((label) => Metadata.ESTEVES_LABELS[label].color);

  const shorelinesData = yield call(() => new Promise((resolve, reject) => shorelines.evaluate(resolve, reject)));
  let newFeatures = [];
  shorelinesData.features.forEach((el) => {
    let coordinates = el.geometry.coordinates;
    if (el.geometry.type === 'LinearRing') coordinates = coordinates.slice(0, coordinates.length - 1);
    let newFeature = ee.Feature(ee.Geometry.LineString(coordinates), el.properties);
    newFeatures.push(newFeature);
  });
  shorelines = ee.FeatureCollection(newFeatures);

  yield put(Map.removeShapeGroup('shoreRCoastlineData'));
  yield put(Map.removeShapeGroup(Metadata.FeatureType.TRANSECT));

  yield put(Map.addEEFeature(shorelines, 'forms.map.shorelines', shoreColors, 1, 'shoreRCoastlineData'));
  yield put(
    Map.addEEFeature(transectsViz, 'forms.map.transects.title', transectColors, 1, Metadata.FeatureType.TRANSECT),
  );

  const rawTransect = finalResults.getInfo();
  const transectData = Array.isArray(rawTransect)
    ? applyRollingSmooth(rawTransect)
    : { ...rawTransect, features: applyRollingSmooth(rawTransect.features) };
  const shorelineData = shorelines.toList(shorelines.size());

  ShoreRAnalysisCache.shorelinesGeoJSON = shorelinesData;

  yield put(
    Results.push('shoreRData', {
      transectData,
      shorelineData,
      shorelinesGeoJSON: shorelinesData,
    }),
  );
};
